import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import Layout from "../../components/global/layout";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import { toast } from "react-toastify";
import FormControl from "../../components/common/FormControl";
import { MODULE_CONFIG } from "./shared";
import {
  FiArrowLeft,
  FiSave,
  FiPlus,
  FiTrash2,
  FiCheck,
  FiX,
  FiDollarSign,
  FiList,
  FiLayers,
} from "react-icons/fi";

export default function PlanAddEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  // Form State (Clean defaults, zero static dummy data)
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    type: "paid",
    quickbooks_integration: "QBO",
    freeDays: "",
    stripe_product_id: "",
    stripe_price_id: "",
    max_users: "",
    max_monthly_quotes: "",
    display_features: [],
  });

  // Validation Errors State (Matching Login Page standard)
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Fetch existing plan details when in Edit mode
  const fetchPlanDetail = useCallback(async () => {
    if (!id) return;
    loader(true);
    try {
      const res = await ApiClient.get(`${MODULE_CONFIG.endpoints.detail}?id=${id}`);
      if (res?.success && res?.data) {
        const d = res.data;
        setFormData({
          name: d.name || "",
          description: d.description || "",
          price: d.price !== undefined ? String(d.price) : "",
          type: d.type || "paid",
          quickbooks_integration: d.quickbooks_integration || "QBO",
          freeDays: d.freeDays !== undefined && d.freeDays !== null ? String(d.freeDays) : "",
          stripe_product_id: d.stripe_product_id || "",
          stripe_price_id: d.stripe_price_id || "",
          max_users:
            d.limits?.max_users !== undefined && d.limits?.max_users !== null
              ? String(d.limits.max_users)
              : "",
          max_monthly_quotes:
            d.limits?.max_monthly_quotes !== undefined && d.limits?.max_monthly_quotes !== null
              ? String(d.limits.max_monthly_quotes)
              : "",
          display_features: Array.isArray(d.display_features)
            ? d.display_features.map((f) => ({
              title: f.title || "",
              included: f.included !== false,
            }))
            : [],
        });
      } else {
        toast.error("Failed to fetch plan details.");
        navigate(MODULE_CONFIG.routeBase);
      }
    } catch (err) {
      console.error("Failed to load plan detail:", err);
      toast.error("Error loading plan details.");
    } finally {
      loader(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (isEdit) {
      fetchPlanDetail();
    }
  }, [isEdit, fetchPlanDetail]);

  // Real-time Field Validation Handlers
  const handleFieldChange = (field, val) => {
    setFormData((prev) => ({ ...prev, [field]: val }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleNameChange = (val) => {
    handleFieldChange("name", val);
    if (!val || !val.trim()) {
      setErrors((prev) => ({ ...prev, name: "Plan name is required." }));
    } else {
      setErrors((prev) => ({ ...prev, name: "" }));
    }
  };

  const handlePriceChange = (val) => {
    handleFieldChange("price", val);
    if (formData.type === "paid" && (val === "" || isNaN(Number(val)) || Number(val) < 0)) {
      setErrors((prev) => ({ ...prev, price: "Please enter a valid price." }));
    } else {
      setErrors((prev) => ({ ...prev, price: "" }));
    }
  };

  const handleTypeChange = (val) => {
    setFormData((prev) => ({
      ...prev,
      type: val,
      price: val === "free" ? "0" : prev.price === "0" ? "" : prev.price,
    }));
    if (val === "free") {
      setErrors((prev) => ({ ...prev, price: "" }));
    }
  };

  // Add / Update / Remove Display Features
  const handleAddDisplayFeature = () => {
    setFormData((prev) => ({
      ...prev,
      display_features: [...prev.display_features, { title: "", included: true }],
    }));
  };

  const handleUpdateDisplayFeature = (index, field, value) => {
    setFormData((prev) => {
      const next = [...prev.display_features];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, display_features: next };
    });
  };

  const handleRemoveDisplayFeature = (index) => {
    setFormData((prev) => ({
      ...prev,
      display_features: prev.display_features.filter((_, i) => i !== index),
    }));
  };

  // Form Submission
  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Plan name is required.";
    }
    if (formData.type === "paid") {
      if (formData.price === "" || isNaN(Number(formData.price)) || Number(formData.price) < 0) {
        newErrors.price = "Valid price is required for paid plans.";
      }
    }
    if (!formData.quickbooks_integration) {
      newErrors.quickbooks_integration = "QuickBooks integration is required.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fill in all required fields.");
      return;
    }

    setErrors({});
    setSubmitting(true);
    loader(true);

    // Platform features are ALWAYS true in payload as required
    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      price: formData.type === "free" ? 0 : Number(formData.price) || 0,
      type: formData.type,
      quickbooks_integration: formData.quickbooks_integration,
      freeDays: formData.freeDays !== "" ? Number(formData.freeDays) : 0,
      features: {
        custom_branding: true,
        custom_domain: true,
      },
      limits: {
        max_users: formData.max_users !== "" ? Number(formData.max_users) : 0,
        max_monthly_quotes:
          formData.max_monthly_quotes !== "" ? Number(formData.max_monthly_quotes) : 0,
      },
      display_features: formData.display_features
        .filter((f) => f.title && f.title.trim())
        .map((f) => ({
          title: f.title.trim(),
          included: Boolean(f.included),
        })),
      stripe_product_id: formData.stripe_product_id.trim() || null,
      stripe_price_id: formData.stripe_price_id.trim() || null,
    };

    if (isEdit) {
      payload.id = id;
    }

    try {
      const endpoint = isEdit ? MODULE_CONFIG.endpoints.update : MODULE_CONFIG.endpoints.add;
      const res = isEdit
        ? await ApiClient.put(endpoint, payload)
        : await ApiClient.post(endpoint, payload);

      if (res?.success) {
        toast.success(isEdit ? "Plan updated successfully." : "Plan created successfully.");
        navigate(MODULE_CONFIG.routeBase);
      } else {
        toast.error(res?.message || res?.error?.message || "Operation failed.");
      }
    } catch (err) {
      console.error("Failed to save plan:", err);
      toast.error("Failed to save plan. Please check form data.");
    } finally {
      setSubmitting(false);
      loader(false);
    }
  };

  return (
    <Layout>
      <div className="w-full max-w-full space-y-5 pb-16 antialiased">
        {/* ======================================================== */}
        {/* 1. TOP HEADER & ACTIONS BANNER */}
        {/* ======================================================== */}
        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3.5 min-w-0">
            <Link
              to={MODULE_CONFIG.routeBase}
              className="p-2.5 rounded-xl bg-slate-200 hover:bg-slate-300 text-slate-900 border border-slate-300 transition-colors cursor-pointer shadow-xs shrink-0 flex items-center justify-center"
              title="Back to Plans"
            >
              <FiArrowLeft className="w-5 h-5 text-slate-900" />
            </Link>
            <div className="space-y-0.5 min-w-0">
              <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight truncate">
                {isEdit ? "Edit Subscription Plan" : "Create Subscription Plan"}
              </h1>
              <p className="text-xs text-slate-800 font-bold">
                {isEdit
                  ? "Update pricing tiers, platform quotas, and customer display bullets."
                  : "Define a new subscription tier with quotas, pricing, and QuickBooks integration."}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 shrink-0">
            <button
              type="button"
              onClick={() => navigate(MODULE_CONFIG.routeBase)}
              className="px-4 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold transition-all border border-slate-300 cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="inline-flex items-center justify-center gap-2 px-5 h-10 rounded-xl bg-[#115B9D] hover:bg-[#0e4b82] text-white text-xs font-bold transition-all shadow-md shadow-[#115B9D]/25 cursor-pointer active:scale-[0.98] disabled:opacity-50"
            >
              <FiSave className="w-4 h-4" />
              <span>{isEdit ? "Save Changes" : "Create Plan"}</span>
            </button>
          </div>
        </div>

        {/* ======================================================== */}
        {/* 2. MAIN FORM (SYMMETRICAL, ZERO DEAD SPACE ON ANY SCREEN) */}
        {/* ======================================================== */}
        <form onSubmit={handleSubmit} noValidate className="space-y-5">
          {/* ======================================================== */}
          {/* 1. TOP SECTION: BALANCED HALF-HALF 2-COLUMN LAYOUT       */}
          {/*    Left (50%): Plan Details & Pricing                    */}
          {/*    Right (50%): Account Quotas & Limits + Stripe Billing  */}
          {/* ======================================================== */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-stretch">
            {/* Card 1: Plan Details & Integration */}
            <div className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] space-y-4 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2.5 border-b border-slate-200 pb-3">
                  <div className="w-7 h-7 rounded-lg bg-blue-50 text-[#115B9D] border border-blue-200 flex items-center justify-center font-bold">
                    <FiLayers className="w-4 h-4" />
                  </div>
                  <h3 className="text-sm font-black text-slate-900 uppercase tracking-wide">
                    Plan Details & Scope
                  </h3>
                </div>

                <div className="space-y-4 pt-3.5">
                  {/* Plan Name */}
                  <FormControl
                    type="text"
                    name="name"
                    id="planName"
                    label="Plan Name"
                    required={true}
                    placeholder="e.g. Basic Online Starter"
                    value={formData.name}
                    error={errors.name}
                    onChange={handleNameChange}
                    divClass="w-full"
                    labelClass="!text-xs !font-bold !uppercase !tracking-wider !text-slate-900 !mb-1"
                    className="!h-10 !rounded-xl text-slate-900 text-sm shadow-none"
                  />

                  {/* QuickBooks Connector */}
                  <FormControl
                    type="select"
                    name="quickbooks_integration"
                    id="quickbooks_integration"
                    label="QuickBooks Connector"
                    theme="search"
                    required={true}
                    placeholder="Select Connector"
                    value={formData.quickbooks_integration}
                    error={errors.quickbooks_integration}
                    options={[
                      { id: "QBO", name: "QuickBooks Online (QBO)" },
                      { id: "QBD", name: "QuickBooks Desktop (QBD)" },
                      { id: "BOTH", name: "Hybrid (Both QBO & QBD)" },
                      { id: "NONE", name: "None (Standalone)" },
                    ]}
                    onChange={(val) => handleFieldChange("quickbooks_integration", val)}
                    divClass="w-full"
                    labelClass="!text-xs !font-bold !uppercase !tracking-wider !text-slate-900 !mb-1"
                    className="!h-10 !rounded-xl text-slate-900 text-sm shadow-none"
                  />

                  {/* Description */}
                  <FormControl
                    type="textarea"
                    name="description"
                    id="planDescription"
                    label="Plan Description & Terms"
                    placeholder="Describe plan features, target customers, and scope..."
                    value={formData.description}
                    onChange={(val) => handleFieldChange("description", val)}
                    divClass="w-full"
                    labelClass="!text-xs !font-bold !uppercase !tracking-wider !text-slate-900 !mb-1"
                    className="!rounded-xl text-slate-900 text-sm shadow-none !min-h-[135px]"
                  />
                </div>
              </div>
            </div>

            {/* Card 2: Pricing & Capacity Limits */}
            <div className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] space-y-5 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2.5 border-b border-slate-200 pb-3">
                  <div className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-800 border border-emerald-200 flex items-center justify-center font-bold">
                    <FiDollarSign className="w-4 h-4" />
                  </div>
                  <h3 className="text-sm font-black text-slate-900 uppercase tracking-wide">
                    Pricing & Resource Limits
                  </h3>
                </div>

                <div className="space-y-4 pt-3.5">
                  {/* Price, Plan Tier & Trial Days */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <FormControl
                      type="number"
                      name="price"
                      id="planPrice"
                      label="Monthly Price($)"
                      required={formData.type === "paid"}
                      disabled={formData.type === "free"}
                      placeholder="19"
                      value={formData.type === "free" ? "0" : formData.price}
                      error={errors.price}
                      onChange={handlePriceChange}
                      divClass="w-full"
                      labelClass="!text-xs !font-bold !uppercase !tracking-wider !text-slate-900 !mb-1"
                      className="!h-10 !rounded-xl text-slate-900 text-sm shadow-none font-bold"
                    />

                    <FormControl
                      type="select"
                      name="type"
                      id="planType"
                      label="Plan Tier"
                      theme="search"
                      required={true}
                      placeholder="Select Tier"
                      value={formData.type}
                      options={[
                        { id: "paid", name: "Paid Tier" },
                        { id: "free", name: "Free Tier" },
                      ]}
                      onChange={handleTypeChange}
                      divClass="w-full"
                      labelClass="!text-xs !font-bold !uppercase !tracking-wider !text-slate-900 !mb-1"
                      className="!h-10 !rounded-xl text-slate-900 text-sm shadow-none"
                    />

                    <FormControl
                      type="number"
                      name="freeDays"
                      id="freeDays"
                      label="Free Trial Days"
                      placeholder="14"
                      value={formData.freeDays}
                      onChange={(val) => handleFieldChange("freeDays", val)}
                      divClass="w-full"
                      labelClass="!text-xs !font-bold !uppercase !tracking-wider !text-slate-900 !mb-1"
                      className="!h-10 !rounded-xl text-slate-900 text-sm shadow-none font-bold"
                    />
                  </div>

                  {/* Quotas & Limits Header & Inputs */}
                  <div className="pt-2 border-t border-slate-100">
                    <div className="flex items-center justify-between pb-2">
                      <span className="text-xs font-black text-slate-900 uppercase tracking-wide">
                        Account Quotas & User Limits
                      </span>
                      <span className="text-[11px] font-bold text-slate-800">
                        Tip: <span className="font-black text-slate-950">-1</span> for unlimited
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      <FormControl
                        type="number"
                        name="max_users"
                        id="maxUsers"
                        label="Max Users"
                        placeholder="e.g. 2 (-1 for unlimited)"
                        value={formData.max_users}
                        onChange={(val) => handleFieldChange("max_users", val)}
                        divClass="w-full"
                        labelClass="!text-xs !font-bold !uppercase !tracking-wider !text-slate-900 !mb-1"
                        className="!h-10 !rounded-xl text-slate-900 text-sm shadow-none font-bold"
                      />

                      <FormControl
                        type="number"
                        name="max_monthly_quotes"
                        id="maxMonthlyQuotes"
                        label="Max Monthly Quotes"
                        placeholder="e.g. 50 (-1 for unlimited)"
                        value={formData.max_monthly_quotes}
                        onChange={(val) => handleFieldChange("max_monthly_quotes", val)}
                        divClass="w-full"
                        labelClass="!text-xs !font-bold !uppercase !tracking-wider !text-slate-900 !mb-1"
                        className="!h-10 !rounded-xl text-slate-900 text-sm shadow-none font-bold"
                      />
                    </div>
                  </div>

                  {/* Informational Callout */}
                  <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/90 text-xs text-slate-700">
                    <span className="font-bold text-slate-900 block mb-0.5">Automated Tenant Enforcement</span>
                    <p className="text-[11px] text-slate-600 leading-relaxed">
                      Assigned limits govern tenant user invitations and monthly quote generation capacity automatically.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ======================================================== */}
          {/* 2. BOTTOM SECTION: MARKETING DISPLAY FEATURES (FULL 100%)*/}
          {/* ======================================================== */}
          <div className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] space-y-4 w-full">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-lg bg-blue-50 text-[#115B9D] border border-blue-200 flex items-center justify-center font-bold">
                  <FiList className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm font-black text-slate-900 uppercase tracking-wide">
                    Marketing Display Features
                  </h3>
                  <p className="text-[11px] font-bold text-slate-800">
                    Bullet points highlighted on pricing comparison tables
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={handleAddDisplayFeature}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-[#115B9D] hover:bg-[#0e4b82] text-white text-xs font-bold transition-all shadow-xs cursor-pointer active:scale-95"
              >
                <FiPlus className="w-4 h-4" />
                <span>Add Bullet</span>
              </button>
            </div>

            {formData.display_features.length === 0 ? (
              <div className="py-8 px-4 text-center rounded-xl border border-dashed border-slate-300 bg-blue-50/20 space-y-3">
                <div className="w-10 h-10 mx-auto rounded-xl bg-blue-50 text-[#115B9D] border border-blue-200 flex items-center justify-center">
                  <FiList className="w-5 h-5" />
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-extrabold text-slate-900">
                    No marketing bullet points configured yet.
                  </p>
                  <p className="text-xs font-bold text-slate-700 max-w-md mx-auto">
                    Add bullet points to highlight key plan features and capabilities on comparison tables.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleAddDisplayFeature}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#115B9D] hover:bg-[#0e4b82] text-white text-xs font-bold transition-all shadow-md shadow-[#115B9D]/20 cursor-pointer"
                >
                  <FiPlus className="w-3.5 h-3.5" />
                  <span>Add First Bullet Point</span>
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                {formData.display_features.map((feature, idx) => (
                  <div
                    key={idx}
                    className="group flex items-center bg-white rounded-xl border border-slate-300 hover:border-slate-400 focus-within:!border-[#115B9D] focus-within:ring-2 focus-within:ring-[#115B9D]/20 transition-all shadow-xs overflow-hidden"
                  >
                    {/* Included / Excluded Toggle Pill */}
                    <button
                      type="button"
                      onClick={() =>
                        handleUpdateDisplayFeature(idx, "included", !feature.included)
                      }
                      className={`h-11 px-3 flex items-center gap-1.5 border-r border-slate-200 transition-colors cursor-pointer shrink-0 select-none ${feature.included
                        ? "bg-emerald-50/80 hover:bg-emerald-100/80 text-emerald-800"
                        : "bg-slate-100 hover:bg-slate-200 text-slate-700"
                        }`}
                      title={
                        feature.included
                          ? "Feature Included in Plan (Click to toggle)"
                          : "Feature Excluded from Plan (Click to toggle)"
                      }
                    >
                      {feature.included ? (
                        <>
                          <span className="w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center shadow-xs">
                            <FiCheck className="w-3.5 h-3.5 stroke-[3]" />
                          </span>
                          <span className="hidden sm:inline text-[11px] font-extrabold text-emerald-900 tracking-tight">
                            Included
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="w-5 h-5 rounded-full bg-slate-400 text-white flex items-center justify-center shadow-xs">
                            <FiX className="w-3.5 h-3.5 stroke-[3]" />
                          </span>
                          <span className="hidden sm:inline text-[11px] font-extrabold text-slate-700 tracking-tight">
                            Excluded
                          </span>
                        </>
                      )}
                    </button>

                    {/* Feature Description Input */}
                    <input
                      type="text"
                      value={feature.title}
                      onChange={(e) =>
                        handleUpdateDisplayFeature(idx, "title", e.target.value)
                      }
                      placeholder="e.g. QuickBooks Online Integration"
                      className="flex-1 h-11 px-3.5 bg-transparent border-0 text-xs sm:text-sm font-bold text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0"
                    />

                    {/* Delete Action Button */}
                    <button
                      type="button"
                      onClick={() => handleRemoveDisplayFeature(idx)}
                      className="h-11 w-11 flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 border-l border-slate-200 transition-colors cursor-pointer shrink-0"
                      title="Delete bullet point"
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ======================================================== */}
          {/* 3. FULL-WIDTH BOTTOM ACTIONS FOOTER (PERFECTLY ANCHORED) */}
          {/* ======================================================== */}
          <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-200">
            <button
              type="button"
              onClick={() => navigate(MODULE_CONFIG.routeBase)}
              className="px-5 h-11 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold transition-all border border-slate-300 cursor-pointer shadow-xs"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center justify-center gap-2 px-6 h-11 rounded-xl bg-[#115B9D] hover:bg-[#0e4b82] text-white text-xs font-bold transition-all shadow-md shadow-[#115B9D]/30 cursor-pointer active:scale-[0.98] disabled:opacity-50"
            >
              <FiSave className="w-4 h-4" />
              <span>{isEdit ? "Update Plan" : "Create Plan"}</span>
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
